import { Paper, Typography } from '@mui/material';
import { fromNullToUndefined, withDefault } from '@vy/utils';
import React from 'react';
import { Helmet } from 'react-helmet';
import { VyLayout } from '../../../components/new-design/layout/VyLayout';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { useProductsPage } from '../../../hooks/requests/pages/use-products-page';
import { useTablePagination } from '../../../hooks/use-table-pagination';
import { useSearchWithSearchParams } from '../../../hooks/use-search-with-search-params';
import { useCatalogProductsList } from '../../../hooks/requests/use-catalog-products-list';
import { useCreditTypesList } from '../../../hooks/requests/use-credit-types-list';
import { ProductsTable } from './products-page-components/ProductsTable/ProductsTable';
import { buildUnifiedProductRow } from './products-page-components/ProductsTable/products-columns/products-columns-utils';
import { filterCatalogProducts } from './products-page-utils';

const ProductsPage: React.FC = () => {
  const [query] = useSearchWithSearchParams();
  const { page, pageSize, onPageChange, onPageSizeChange } = useTablePagination();
  const { data: catalogProductsData, isLoading: isLoadingCatalogProducts } = useCatalogProductsList({
    suspense: false,
  });
  const { data: creditTypesData, isLoading: isLoadingCreditTypes } = useCreditTypesList(
    { suspense: false },
    { search: fromNullToUndefined(query) || undefined },
  );

  const { data: productsPageResponse, isLoading: isLoadingProducts } = useProductsPage(
    {
      search: fromNullToUndefined(query),
      page,
      pageSize,
    },
    { suspense: false },
  );

  const meters = withDefault(productsPageResponse?.meters, []);
  const catalogProducts = withDefault(catalogProductsData?.catalogProducts, []);
  const creditTypes = withDefault(creditTypesData?.creditTypes, []);

  const filteredCatalogProducts = filterCatalogProducts(catalogProducts, query);

  const unifiedProducts = buildUnifiedProductRow(meters, filteredCatalogProducts, creditTypes);

  const isLoading = isLoadingProducts || isLoadingCatalogProducts || isLoadingCreditTypes;

  const countedFilteredMeters = withDefault(productsPageResponse?.countedFilteredMeters, 0);
  const itemsCount = countedFilteredMeters + filteredCatalogProducts.length + creditTypes.length;

  return (
    <div className="flex h-full w-full flex-col">
      <Helmet>
        <title>Products</title>
      </Helmet>
      <VyLayout
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography
          sx={{
            fontSize: 24,
            padding: 2
          }}>
          Products Catalog
        </Typography>

        <Paper
          elevation={1}
          sx={{
            height: '100%',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <ProductsTable
            items={unifiedProducts}
            itemsCount={itemsCount}
            loadingItems={isLoading}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </Paper>
      </VyLayout>
    </div>
  );
};

const ProductsPageWithSuspenseBoundary = withSuspenseBoundary(withGlobalSearchParams(ProductsPage));
export { ProductsPageWithSuspenseBoundary as ProductsPage };
